import { useState, useEffect, useRef, useContext } from 'react';
import { Badge } from '@/types';
import { AppContext } from '@/context/AppContext';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Sparkles, Trophy, Award, Crown, Star } from 'lucide-react';
import { EnhancedBadgeIcon } from '@/components/badges/EnhancedBadgeIcon';

interface BadgeUnlockModalProps {
  badge: Badge;
  isOpen: boolean;
  onClose: () => void;
}

function BadgeUnlockModal({ badge, isOpen, onClose }: BadgeUnlockModalProps) {
  const getRarityIcon = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return Crown;
      case 'epic': return Award;
      case 'rare': return Trophy;
      default: return Star;
    }
  };

  const getRarityTextColor = (rarity: string) => {
    switch (rarity) {
      case 'legendary': return 'text-warning';
      case 'epic': return 'text-energy';
      case 'rare': return 'text-primary';
      default: return 'text-success';
    }
  };

  const RarityIcon = getRarityIcon(badge.rarity || 'common');
  const rarityType = (badge.rarity || 'common') as 'common' | 'rare' | 'epic' | 'legendary';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-background/95 backdrop-blur-sm border-2 overflow-hidden">
        <div className="relative p-6 text-center">
          {/* Animated background sparkles */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <Sparkles
                key={i}
                className={`absolute w-4 h-4 text-warning opacity-70 animate-badge-sparkle`}
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`,
                }}
              />
            ))}
          </div>

          {/* Header */}
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-primary mb-2 animate-fade-in">
              Badge Unlocked!
            </h2>
          </div>

          {/* Enhanced Badge Display */}
          <div className="relative mb-6 animate-scale-in flex justify-center">
            <EnhancedBadgeIcon
              badgeId={badge.id}
              rarity={rarityType}
              size="xl"
              earned={true}
            />
          </div>

          {/* Badge Info */}
          <div className="space-y-3 mb-6">
            <h3 className="text-2xl font-bold text-foreground">
              {badge.name}
            </h3>
            
            <p className="text-muted-foreground text-sm leading-relaxed">
              {badge.description}
            </p>
            
            <div className="flex items-center justify-center gap-2">
              <RarityIcon className={`w-4 h-4 ${getRarityTextColor(badge.rarity || 'common')}`} />
              <span className={`text-sm font-semibold capitalize ${getRarityTextColor(badge.rarity || 'common')}`}>
                {badge.rarity} Badge
              </span>
            </div>
          </div>

          {/* Close Button */}
          <Button 
            onClick={onClose}
            className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary text-primary-foreground font-semibold py-3 text-lg transition-all duration-200 hover:scale-105"
          >
            Awesome!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function BadgeUnlockNotifications() {
  const context = useContext(AppContext);
  
  // Return early if context is not available (component rendered outside provider)
  if (!context) {
    return null;
  }
  
  const { state, loading } = context;
  const [unlockedBadge, setUnlockedBadge] = useState<Badge | null>(null);
  const [showModal, setShowModal] = useState(false);
  const previousBadgesRef = useRef<Badge[]>([]);

  // Initialize with current badges on first render
  useEffect(() => {
    if (loading || !state.isAuthenticated) return;
    if (previousBadgesRef.current.length === 0 && state.badges) {
      previousBadgesRef.current = [...state.badges];
    }
  }, [state.badges, loading, state.isAuthenticated]);

  useEffect(() => {
    // Don't process notifications while loading or not authenticated
    if (loading || !state.isAuthenticated) return;

    const previousBadges = previousBadgesRef.current;
    const currentBadges = state.badges || [];

    console.log('Badge notification check:', {
      previousCount: previousBadges.length,
      currentCount: currentBadges.length,
      currentUnlocked: currentBadges.filter(b => b.unlockedAt).length
    });

    // Check for newly unlocked badges
    const newlyUnlocked = currentBadges.filter(currentBadge => {
      const prevBadge = previousBadges.find(prev => prev.id === currentBadge.id);
      const isNewlyUnlocked = currentBadge.unlockedAt && (!prevBadge || !prevBadge.unlockedAt);
      
      if (isNewlyUnlocked) {
        console.log(`Newly unlocked badge detected: ${currentBadge.name}`, currentBadge);
      }
      
      return isNewlyUnlocked;
    });

    // Show modal for the first newly unlocked badge
    if (newlyUnlocked.length > 0) {
      console.log(`Showing badge notification for: ${newlyUnlocked[0].name}`);
      setUnlockedBadge(newlyUnlocked[0]);
      setShowModal(true);
    }

    // Update the previous badges reference
    previousBadgesRef.current = [...currentBadges];
  }, [state.badges, loading, state.isAuthenticated]);

  const handleCloseModal = () => {
    setShowModal(false);
    setUnlockedBadge(null);
  };

  // Don't render anything if loading or not authenticated
  if (loading || !state.isAuthenticated) {
    return null;
  }

  return unlockedBadge ? (
    <BadgeUnlockModal
      badge={unlockedBadge}
      isOpen={showModal}
      onClose={handleCloseModal}
    />
  ) : null;
}
